import {
  useGetGovernorStatusQuery,
  useGetGovernorLogQuery,
  useTickGovernorNowMutation,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { fmtTime } from "@/lib/format";
import { toast } from "sonner";
import { StalenessBadge } from "@/components/domain/StalenessBadge";

/**
 * GovernorCard — surfaces the engine governor's live regime score
 * and engine state on Overview.
 *
 * Three sections:
 *   1) Score + regime badge + the 5 signal contributions
 *   2) Current engine state (which are ACTIVE vs paused, color-coded)
 *   3) Last 5 governor decisions (arm/pause/hold) with timestamp
 *
 * Polls every 60s. "Tick now" forces an immediate evaluation
 * (still respects hysteresis on the backend).
 */
export function GovernorCard() {
  const status = useGetGovernorStatusQuery(undefined, { pollingInterval: 60000 });
  const log = useGetGovernorLogQuery(5, { pollingInterval: 60000 });
  const [tickNow, { isLoading: ticking }] = useTickGovernorNowMutation();

  const s = status.data;

  async function onTick() {
    try {
      const r: any = await tickNow().unwrap();
      const verb = r?.action === "arm" ? "armed engines" :
                   r?.action === "pause" ? "paused engines" :
                   "no state change";
      toast.success(`tick complete — ${verb}`);
    } catch (e: any) {
      toast.error(`tick failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  if (status.isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle>Engine governor</CardTitle></CardHeader>
        <CardContent><Skeleton className="h-32 w-full" /></CardContent>
      </Card>
    );
  }
  if (!s) return null;

  const regimeColor = s.regime === "favorable" ? "good"
                    : s.regime === "hostile"   ? "bad"
                                                : "warn";
  const scoreColor = s.score >= 65 ? "text-green-500"
                   : s.score <= 35 ? "text-red-500"
                                    : "text-amber-500";

  // Parse engines_after JSON from latest log entry to show current state
  let currentEngines: Array<{ label: string; is_active: boolean }> = [];
  if (log.data && log.data.length > 0 && log.data[0].engines_after) {
    try {
      const obj = JSON.parse(log.data[0].engines_after);
      currentEngines = Object.entries(obj).map(([label, st]: [string, any]) => ({
        label, is_active: !!st.is_active,
      }));
    } catch {}
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex items-baseline gap-2 flex-wrap">
          <span>Engine governor</span>
          <Badge tone={regimeColor}>
            {s.regime.toUpperCase()}
          </Badge>
          <span className={`text-lg font-bold tabular-nums ${scoreColor}`}>
            {s.score.toFixed(0)}/100
          </span>
          <StalenessBadge ts={(log.data ?? [])[0]?.ts} warnHours={1} staleHours={2} veryStaleHours={6} label="tick" />
          <span className="text-xs text-muted font-normal">
            auto-flips opportunity/breakout/library/scalp based on market regime
          </span>
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={onTick} disabled={ticking}>
          {ticking ? "Ticking…" : "Tick now"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Signal breakdown */}
        <div>
          <div className="text-xs text-muted mb-1">score breakdown:</div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-1.5 text-xs">
            {s.breakdown.map((b, i) => {
              const delta = b.delta ?? 0;
              const cls = delta > 0 ? "text-green-500"
                        : delta < 0 ? "text-red-500"
                                     : "text-muted";
              return (
                <div key={i} className="border rounded p-1.5">
                  <div className="text-muted">{b.signal}</div>
                  <div className={`font-semibold ${cls}`}>
                    {delta > 0 ? "+" : ""}{delta}
                  </div>
                  <div className="text-[10px] text-muted truncate" title={b.tag ?? b.error ?? ""}>
                    {b.tag ?? b.error ?? "—"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Engine state */}
        {currentEngines.length > 0 && (
          <div>
            <div className="text-xs text-muted mb-1">engine state:</div>
            <div className="flex flex-wrap gap-1.5">
              {currentEngines.map((e) => (
                <Badge key={e.label} tone={e.is_active ? "good" : "muted"}>
                  {e.label}: {e.is_active ? "ACTIVE" : "paused"}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Last 5 governor decisions */}
        {log.data && log.data.length > 0 && (
          <div>
            <div className="text-xs text-muted mb-1">last 5 decisions:</div>
            <div className="space-y-0.5 text-xs">
              {log.data.slice(0, 5).map((r) => {
                const aColor = r.action === "arm" ? "good"
                             : r.action === "pause" ? "warn"
                             : r.action === "hysteresis_block" ? "info"
                                                                  : "muted";
                return (
                  <div key={r.id} className="flex items-baseline gap-2">
                    <span className="text-muted whitespace-nowrap tabular-nums">
                      {fmtTime(r.ts)}
                    </span>
                    <Badge tone={aColor}>{r.action}</Badge>
                    <span className="text-muted">→</span>
                    <span className="tabular-nums">
                      score {r.score.toFixed(0)}, regime {r.regime}
                    </span>
                    {r.note && (
                      <span className="text-muted text-[10px] truncate">
                        ({r.note})
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
